import { formatDate } from '@frontend/mwm-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import classNames from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/ReactHookForm/DateInput'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS, TODAY } from '~/const'
import { EVENTS } from '~/const/catalog'
import { keys as catalogKeys, useCreateDailyPostMutation, useDailyPostsQuery } from '~/queries/catalog'
import dayjs from '~/utils/dayjs'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Values {
  date: string
}

const validationSchema = Yup.object().shape({
  date: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  onClose: VoidFunction
  selectedTemplate: string
}

const DateForm = ({ onClose, selectedTemplate }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    resetField,
  } = useForm<Values>({ resolver: yupResolver(validationSchema) })

  const today = dayjs(TODAY).startOf('day').toDate()

  /** Queries & Mutations */

  const { data: dailyPostsData = [] } = useDailyPostsQuery({
    endDate: dayjs(TODAY).add(2, 'years').format('YYYY-MM-DD'),
    startDate: dayjs(TODAY).subtract(2, 'years').format('YYYY-MM-DD'),
  })

  const daysWithDailyPost = dailyPostsData?.map(({ day }) => day)

  const { mutateAsync: createDailyPostMutation } = useCreateDailyPostMutation()

  /** Handlers */

  const getDayClassName = (day: Date) => {
    return classNames({
      'react-datepicker__day--before-today': day < today,
      'react-datepicker__day--with-drawing': daysWithDailyPost.includes(formatDate(day, 'YYYY-MM-DD')),
    })
  }

  const handleFormSubmit = async (values: Values) => {
    const formattedDate = new Date(values.date).toLocaleDateString('en-CA')
    try {
      await createDailyPostMutation({
        coloring_template_id: selectedTemplate,
        day: formattedDate,
      })
      await queryClient.invalidateQueries({ queryKey: catalogKeys.searchTemplates('') })
      await queryClient.invalidateQueries({
        queryKey: catalogKeys.dailyPosts({
          endDate: dayjs(values.date).endOf('month').format('YYYY-MM-DD'),
          startDate: dayjs(values.date).startOf('month').format('YYYY-MM-DD'),
        }),
      })
      onClose()
      displayToast(`Template successfully set to ${formattedDate}`, 'success')
      resetField('date')
    } catch (e) {
      captureException(e)
      displayToast(isAxiosError(e) ? `Something went wrong: ${e.message}` : 'Something went wrong', 'error')
    }
  }

  /** Render */

  return (
    <>
      <ModalHeader>Add to Daily</ModalHeader>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody>
          <Controller
            control={control}
            name="date"
            render={({ field: { onChange } }) => (
              <DateInput
                className="daily-modal-datepicker"
                dayClassName={getDayClassName}
                error={errors.date?.message}
                holidays={EVENTS}
                label="Pick a date you want to assign the template to"
                onDateChange={onChange}
                placeholder="Select a date"
                required
              />
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            Add
          </Button>
        </ModalFooter>
      </form>
    </>
  )
}

export default DateForm
